import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, X, Building2, User, FileText, Package, Activity, Lightbulb, TrendingUp, HeartPulse, ReceiptText } from "lucide-react";
import api, { formatApiError, paymentLabel } from "@/lib/api";
import { openInvoicePdf } from "@/lib/pdf";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TYPES = ["individual", "company", "fleet", "repair_shop", "dealership", "distributor"];
const TERMS = ["prepaid", "net_15", "net_30", "net_60"];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (d) => (d ? String(d).slice(0, 10) : "—");
const STY = { paid: "bg-emerald-100 text-emerald-700", unpaid: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700", completed: "bg-emerald-100 text-emerald-700", refunded: "bg-red-100 text-red-700", voided: "bg-zinc-200 text-zinc-600" };

export default function CustomerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["owner", "manager", "cashier", "sales"].includes(user.role);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/customers/${id}/profile`); setData(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); navigate("/customers"); }
  };
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    try {
      await api.put(`/customers/${id}`, form);
      toast.success("Customer updated"); setForm(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!data) return <div className="h-full flex items-center justify-center text-zinc-400">Loading…</div>;
  const { customer: c, metrics: m, payment_health: ph, customer_value: cv, top_products, recent_activity, recent_invoices, insights } = data;
  const isCompany = (c.customer_type || "individual") !== "individual";

  return (
    <div className="h-full overflow-y-auto p-6" data-testid="customer-details">
      <div className="flex items-center justify-between gap-4 mb-6">
        <button onClick={() => navigate("/customers")} data-testid="cd-back" className="h-10 px-4 rounded-xl border border-zinc-200 bg-white font-semibold flex items-center gap-2 active:scale-95"><ArrowLeft size={17} />Back</button>
        {canEdit && <button onClick={() => setForm({ ...c })} data-testid="cd-edit" className="h-10 px-5 rounded-xl bg-brand text-white font-semibold flex items-center gap-2 active:scale-95"><Pencil size={16} />Edit customer</button>}
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="h-14 w-14 rounded-2xl bg-brand-soft text-brand flex items-center justify-center">{isCompany ? <Building2 size={26} /> : <User size={26} />}</div>
        <div className="min-w-0">
          <h1 className="font-heading text-3xl font-bold tracking-tighter truncate">{c.company_name || c.name || "Customer"}</h1>
          <p className="text-zinc-400 text-sm capitalize">{(c.customer_type || "individual").replace("_", " ")} · {(c.credit_terms || "prepaid").replace("_", " ")}</p>
        </div>
      </div>

      {/* KPI row: Boxes 2-5 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Kpi label="Total invoices" value={m.total_invoices} testid="cd-total-invoices" />
        <Kpi label="Average invoice" value={money(m.avg_invoice)} testid="cd-avg-invoice" />
        <Kpi label="Total sales" value={money(m.total_sales)} testid="cd-total-sales" />
        <Kpi label="Outstanding" value={money(m.outstanding)} accent={m.outstanding > 0 ? "text-amber-600" : "text-emerald-600"} testid="cd-outstanding" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Box 1 — Customer information */}
        <Card title="Customer information" icon={<User size={16} />} testid="cd-info">
          <dl className="space-y-3 text-sm">
            <Row k="Company name" v={c.company_name || "—"} />
            <Row k="Customer name" v={c.name || "—"} />
            <Row k="Phone number" v={c.phone || "—"} />
            <Row k="E-mail" v={c.email || "—"} />
            <Row k="Customer since" v={day(c.created_at)} />
            <Row k="Last purchase" v={day(data.last_purchase)} />
          </dl>
        </Card>

        {/* Box 7 — Payment health */}
        <Card title="Payment health" icon={<HeartPulse size={16} />} testid="cd-payment-health">
          <div className="space-y-3">
            <HealthRow label="Paid" amt={ph.paid_amount} count={ph.paid_count} color="bg-emerald-500" />
            <HealthRow label="Unpaid" amt={ph.unpaid_amount} count={ph.unpaid_count} color="bg-amber-500" />
            <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
              <span className="text-sm font-semibold text-zinc-500">Due</span>
              <span className="font-heading text-lg font-extrabold text-amber-600">{money(ph.due)}</span>
            </div>
          </div>
        </Card>

        {/* Box 8 — Customer value */}
        <Card title="Customer value" icon={<TrendingUp size={16} />} testid="cd-customer-value">
          <div className="grid grid-cols-2 gap-4">
            <Mini k="Lifetime revenue" v={money(cv.lifetime_revenue)} />
            <Mini k="Purchases" v={cv.purchases} />
            <Mini k="Average order" v={money(cv.avg_order)} />
            <Mini k="Frequency / month" v={cv.frequency_per_month} />
          </div>
        </Card>

        {/* Box 6 — Top products */}
        <Card title="Top products" icon={<Package size={16} />} testid="cd-top-products">
          {top_products.length === 0 ? <Empty text="No purchases yet." /> : (
            <ul className="space-y-3">
              {top_products.map((p, i) => (
                <li key={i} className="flex items-center justify-between gap-3" data-testid={`cd-top-product-${i}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{p.name}</p>
                    <p className="text-xs text-zinc-400">{p.quantity} units{p.part_number ? ` · ${p.part_number}` : ""}</p>
                  </div>
                  <span className="font-semibold text-sm whitespace-nowrap">{money(p.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Box 11 — Insights */}
        <Card title="Insights" icon={<Lightbulb size={16} />} testid="cd-insights">
          {insights.length === 0 ? <Empty text="Not enough data yet." /> : (
            <ul className="space-y-2.5">
              {insights.map((t, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-zinc-600" data-testid={`cd-insight-${i}`}>
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brand shrink-0" />{t}
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Box 9 — Recent activity */}
        <Card title="Recent activity" icon={<Activity size={16} />} testid="cd-recent-activity">
          {recent_activity.length === 0 ? <Empty text="No activity yet." /> : (
            <ul className="space-y-3">
              {recent_activity.map((a, i) => (
                <li key={i} className="flex items-center justify-between gap-3" data-testid={`cd-activity-${i}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate capitalize">{a.label}</p>
                    <p className="text-xs text-zinc-400">{day(a.date)} · <span className="capitalize">{(a.status || "").replace("_", " ")}</span></p>
                  </div>
                  <span className="text-sm font-semibold whitespace-nowrap">{money(a.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Box 10 — Recent invoices (full width) */}
        <div className="lg:col-span-3">
          <Card title="Recent invoices" icon={<ReceiptText size={16} />} testid="cd-recent-invoices">
            {recent_invoices.length === 0 ? <Empty text="No invoices yet." /> : (
              <div className="overflow-x-auto -mx-5 -mb-2">
                <table className="w-full text-sm">
                  <thead className="text-left text-zinc-400 uppercase text-[11px] tracking-wider">
                    <tr><th className="px-5 py-2">Invoice</th><th className="px-5 py-2">Date</th><th className="px-5 py-2">Amount</th><th className="px-5 py-2">Status</th><th className="px-5 py-2">Due</th><th className="px-5 py-2 text-right">Action</th></tr>
                  </thead>
                  <tbody>
                    {recent_invoices.map((inv) => (
                      <tr key={inv.id} className="border-t border-zinc-100" data-testid={`cd-invoice-${inv.id}`}>
                        <td className="px-5 py-3 font-semibold whitespace-nowrap">{inv.invoice_number}</td>
                        <td className="px-5 py-3 text-zinc-500">{day(inv.date)}</td>
                        <td className="px-5 py-3 font-semibold">{money(inv.total)}</td>
                        <td className="px-5 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STY[inv.payment_status] || "bg-zinc-100 text-zinc-600"}`}>{inv.payment_status}</span></td>
                        <td className="px-5 py-3 text-amber-600 font-semibold">{inv.due > 0 ? money(inv.due) : "—"}</td>
                        <td className="px-5 py-3 text-right"><button onClick={() => openInvoicePdf(inv.id)} data-testid={`cd-view-invoice-${inv.id}`} className="h-8 px-3 rounded-lg border border-zinc-200 font-semibold inline-flex items-center gap-1.5 active:scale-95"><FileText size={14} />View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="cd-edit-modal">
            <div className="flex items-center justify-between p-5 border-b border-zinc-100 sticky top-0 bg-white"><h2 className="font-heading text-xl font-bold">Edit customer</h2><button onClick={() => setForm(null)} className="h-9 w-9 rounded-lg hover:bg-zinc-100 flex items-center justify-center"><X size={18} /></button></div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <Sel label="Type" v={form.customer_type} set={(v) => setForm({ ...form, customer_type: v })} opts={TYPES} testid="cd-f-type" />
              <Sel label="Credit terms" v={form.credit_terms} set={(v) => setForm({ ...form, credit_terms: v })} opts={TERMS} testid="cd-f-terms" />
              <F label="Company name" v={form.company_name} set={(v) => setForm({ ...form, company_name: v })} testid="cd-f-company" />
              <F label="Contact name" v={form.name} set={(v) => setForm({ ...form, name: v })} testid="cd-f-name" />
              <F label="Email" v={form.email} set={(v) => setForm({ ...form, email: v })} testid="cd-f-email" />
              <F label="Phone" v={form.phone} set={(v) => setForm({ ...form, phone: v })} testid="cd-f-phone" />
              <F label="Tax ID" v={form.tax_id} set={(v) => setForm({ ...form, tax_id: v })} testid="cd-f-tax" />
              <F label="Billing address" v={form.billing_address} set={(v) => setForm({ ...form, billing_address: v })} testid="cd-f-billing" />
              <F label="Shipping address" v={form.shipping_address} set={(v) => setForm({ ...form, shipping_address: v })} testid="cd-f-shipping" full />
              <button onClick={save} data-testid="cd-f-save" className="col-span-2 h-12 rounded-xl bg-brand text-white font-semibold active:scale-[0.98]">Save customer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  return <div data-testid={testid} className="bg-white border border-zinc-200/70 rounded-2xl p-5"><p className="text-[11px] uppercase tracking-widest font-bold text-zinc-400">{label}</p><p className={`font-heading text-2xl font-extrabold mt-2 ${accent || ""}`}>{value}</p></div>;
}
function Card({ title, icon, children, testid }) {
  return <div data-testid={testid} className="bg-white border border-zinc-200/70 rounded-2xl p-5"><div className="flex items-center gap-2 mb-4 text-brand"><span className="h-7 w-7 rounded-lg bg-brand-soft flex items-center justify-center">{icon}</span><h3 className="font-heading font-bold text-zinc-800">{title}</h3></div>{children}</div>;
}
function Row({ k, v }) { return <div className="flex items-center justify-between gap-3"><dt className="text-zinc-400">{k}</dt><dd className="font-semibold text-right truncate max-w-[60%]">{v}</dd></div>; }
function Mini({ k, v }) { return <div><p className="text-[11px] uppercase tracking-widest font-bold text-zinc-400">{k}</p><p className="font-heading text-xl font-extrabold mt-1">{v}</p></div>; }
function HealthRow({ label, amt, count, color }) {
  return <div className="flex items-center justify-between"><div className="flex items-center gap-2.5"><span className={`h-2.5 w-2.5 rounded-full ${color}`} /><span className="text-sm font-semibold text-zinc-600">{label} <span className="text-zinc-400 font-normal">({count})</span></span></div><span className="font-semibold text-sm">${Number(amt || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>;
}
function Empty({ text }) { return <p className="text-sm text-zinc-400 py-4 text-center">{text}</p>; }
function F({ label, v, set, testid, full }) { return <div className={full ? "col-span-2" : ""}><label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{label}</label><input data-testid={testid} value={v || ""} onChange={(e) => set(e.target.value)} className="inp mt-1.5" /></div>; }
function Sel({ label, v, set, opts, testid }) { return <div><label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{label}</label><select data-testid={testid} value={v} onChange={(e) => set(e.target.value)} className="inp mt-1.5 capitalize">{opts.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}</select></div>; }
